import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { HeaderNav } from '../components/HeaderNav';
import { roadmapApi } from '../services/api';
import type { AlignmentRow, Roadmap } from '../roadmapTypes';

/**
 * The ADLC landing view (plans/barnyard/ROADMAP_APP.md Phase 3).
 *
 * WHAT THIS REPLACES, AND WHY IT IS NOT A NICER GALLERY
 *
 * Phase 3 asks for "the landing view that unites Fuddy / Domains / Roadmap into one ADLC
 * narrative rather than three cards in a list". Three cards in a list is what `/viz` already
 * is, and the problem with that layout is not that it looks like a list — it is that the
 * artifacts appear to be unrelated things, so nobody can say what the platform is *doing*
 * without opening all of them and holding them in their head at once.
 *
 * The narrative that unites them already exists and is not invented here: it is
 * ADP-PLATFORM-OVERVIEW.md §8, "How a unit of work moves through ADP" — Signal, Mission,
 * Execution, Verdict, Corpus. Each artifact is evidence for one stage of that spine.
 *
 * THE RULE THIS PAGE IS BUILT AGAINST (ROADMAP_APP.md §4): *if a lane cannot disagree with
 * another lane, it does not belong on this page. A roadmap that cannot go red is a deck.* A
 * landing page of five prettily-labelled stages is precisely a deck, so this one is wired to
 * state that can contradict it, in three independent ways:
 *
 *   1. The health band reads `/api/roadmap` and shows the real alignment counts. When the
 *      CLAIMED lane disagrees with the SHIPPED lane, this page says so on arrival.
 *   2. A stage with no evidence artifact renders as `unevidenced` and is COUNTED. That is
 *      the narrative's own absence-is-not-a-zero: a spine presented as five complete stages
 *      when two have nothing behind them is exactly the overclaim the roadmap exists to
 *      catch, committed by the page that reports overclaims.
 *   3. Every visualization is named by its GALLERY ID and resolved against the live
 *      `/viz/manifest.json` — see the next block, which is the one that was measured wrong.
 *
 * WHY EVIDENCE IS AN ID AND NOT A URL. The first draft of this file hard-coded
 * `/viz/scenario-data-flow.html` for the CNC data-flow page, inferred from its gallery id.
 * The real `src` is `/viz/dragons.html`. That link would have rendered, styled correctly,
 * and 404'd — a dead link on the page whose entire subject is claims that nothing stands
 * behind. Resolving through the manifest means a renamed or deleted page surfaces HERE, as
 * `unresolved`, instead of surviving as a plausible-looking href nobody clicks.
 *
 * THE STAGE→ARTIFACT MAPPING IS EDITORIAL, AND SAYS SO. Which visualization best evidences
 * which stage is a judgement, not something derivable from the data — so it lives here as
 * one reviewable list rather than being inferred per render from titles or tags. Same call
 * Phase 2 made when it refused theme and product filters: a taxonomy the artifact does not
 * carry must be declared in one place where it can be disagreed with, never guessed at
 * render time where it silently mis-buckets.
 */

interface Evidence {
  /** A `/viz` gallery id, resolved through the manifest. Mutually exclusive with `route`. */
  viz?: string;
  /** An in-app route. Valid by construction — the router is the thing that checks it. */
  route?: string;
  label: string;
  note: string;
}

interface Stage {
  id: string;
  name: string;
  /** Condensed from ADP-PLATFORM-OVERVIEW.md §8's "How a unit of work moves through ADP". */
  what: string;
  evidence: Evidence[];
}

const STAGES: Stage[] = [
  {
    id: 'signal',
    name: 'Signal',
    what: 'A ticket, alert, KPI or request becomes a typed work item with a tenant and an owner.',
    evidence: [],
  },
  {
    id: 'mission',
    name: 'Mission',
    what: 'Decomposed into steps with least-privilege tools, a budget and a deadline.',
    evidence: [
      {
        viz: 'mlaas-moat',
        label: 'The model moat — Fuddy today',
        note: 'What the autonomous task agent does with a mission, and what the same runtime becomes once its verdicts start scoring workers.',
      },
    ],
  },
  {
    id: 'execution',
    name: 'Execution',
    what: 'Agents run in real workspaces with governed tools; every step is journalled before the next begins.',
    evidence: [
      {
        route: '/',
        label: 'Journals',
        note: 'The per-step record this stage is defined by — not a description of it.',
      },
      {
        viz: 'scenario-data-flow',
        label: 'CNC scenarios — data flow',
        note: 'Where a governed domain tool call actually goes once an agent makes it.',
      },
    ],
  },
  {
    id: 'verdict',
    name: 'Verdict',
    what: 'A build, a test suite, a live probe or a solver closes the work by exit code — not by opinion.',
    evidence: [
      {
        route: '/roadmap',
        label: 'Five-lane roadmap',
        note: 'Claimed against planned against shipped against deployed. The disagreements are the verdict on the documents.',
      },
      {
        viz: 'releases',
        label: "What's landed in dev",
        note: 'The merge history the SHIPPED lane is read from.',
      },
    ],
  },
  {
    id: 'corpus',
    name: 'Corpus',
    what: 'That same verdict scores the worker and labels a training example. One signal, three uses.',
    evidence: [],
  },
];

/**
 * Where the platform is named and addressed. Deliberately NOT forced into a §8 stage: the
 * domain decision spans the whole lifecycle rather than sitting inside one step of it, and
 * filing it under "Signal" to make the grid tidy would be the confident mis-bucketing this
 * file's docstring rejects two paragraphs above.
 */
const CROSS_CUTTING: Evidence[] = [
  {
    viz: 'domain-portfolio-mapping',
    label: 'Root domain & taxonomy',
    note: 'Which root domain the platform norms on, and the subdomain taxonomy under it — a decision that touches every stage and belongs to none.',
  },
];

interface ManifestEntry {
  id: string;
  src: string;
  title?: string;
}

/** `read` carries entries; the other two states must never be rendered as "no artifacts". */
type ManifestState =
  | { state: 'loading' }
  | { state: 'read'; byId: Map<string, ManifestEntry> }
  | { state: 'unreadable'; detail: string };

function EvidenceLink({ ev, manifest }: { ev: Evidence; manifest: ManifestState }) {
  const linkClass =
    'text-xs font-semibold text-tower-text underline decoration-tower-accent underline-offset-2 hover:text-tower-accent transition-colors';

  let body;
  if (ev.route) {
    body = (
      <Link to={ev.route} className={linkClass}>
        {ev.label}
      </Link>
    );
  } else if (manifest.state === 'loading') {
    body = (
      <span className="text-xs font-semibold text-tower-muted" data-evidence-state="loading">
        {ev.label}
      </span>
    );
  } else if (manifest.state === 'unreadable') {
    // NOT a link to a guessed path, and not silently dropped. The gallery could not be read,
    // so we do not know whether this artifact exists — a different statement from either
    // "here it is" or "it is gone", and the page has to be able to make it.
    body = (
      <span className="text-xs font-semibold text-tower-text" data-evidence-state="unknown">
        {ev.label}{' '}
        <span className="font-mono font-normal text-[10px]">(gallery unreadable)</span>
      </span>
    );
  } else {
    const hit = manifest.byId.get(ev.viz!);
    body = hit ? (
      <a href={hit.src} className={linkClass} data-evidence-state="resolved">
        {ev.label}
      </a>
    ) : (
      <span className="text-xs font-semibold text-tower-text" data-evidence-state="unresolved">
        {ev.label}{' '}
        <span className="font-mono font-normal text-[10px]">
          (no gallery entry <span className="font-bold">{ev.viz}</span>)
        </span>
      </span>
    );
  }

  return (
    <li data-evidence={ev.viz ?? ev.route}>
      {body}
      <div className="text-[11px] text-tower-muted leading-snug mt-0.5">{ev.note}</div>
    </li>
  );
}

function StageCard({ stage, manifest }: { stage: Stage; manifest: ManifestState }) {
  const unevidenced = stage.evidence.length === 0;
  return (
    <div
      data-stage={stage.id}
      data-evidenced={unevidenced ? 'false' : 'true'}
      className={`p-4 rounded-lg border flex flex-col ${
        unevidenced ? 'bg-warning-bg border-warning' : 'bg-tower-surface border-tower-border'
      }`}
    >
      <div className="text-[10px] uppercase tracking-wider font-mono text-tower-text">
        {stage.name}
      </div>
      <p className="text-xs text-tower-text mt-1 leading-relaxed">{stage.what}</p>
      <div className="flex-1" />
      {unevidenced ? (
        // NOT an empty card, and not omitted from the grid. A stage with nothing behind it
        // is a hole in the story, and the story is what this page is asserting.
        <div className="mt-3 text-[11px] text-tower-text leading-snug">
          <span className="font-mono font-bold">unevidenced</span> — nothing on this site shows
          this stage working. It is in the narrative because §8 puts it there, not because we
          can demonstrate it.
        </div>
      ) : (
        <ul className="mt-3 space-y-2">
          {stage.evidence.map((e) => (
            <EvidenceLink key={e.viz ?? e.route} ev={e} manifest={manifest} />
          ))}
        </ul>
      )}
    </div>
  );
}

/** The live band. Its job is to be capable of being bad news on arrival. */
function Health({ data, error }: { data: Roadmap | null; error: string | null }) {
  const measured = useMemo(
    () => (data?.alignment ?? []).filter((a) => a.state === 'measured'),
    [data],
  );
  const unmeasured = useMemo(
    () => (data?.alignment ?? []).filter((a) => a.state === 'unmeasured'),
    [data],
  );

  if (error) {
    return (
      <div
        data-health="unreadable"
        className="p-4 rounded-lg border bg-error-bg border-error text-sm text-tower-text"
      >
        The roadmap could not be read (<span className="font-mono">{error}</span>), so this page
        is showing the narrative with no live state behind it. That is a worse state than a red
        count, not a better one — this route needs a signed-in session, and an authentication
        failure is indistinguishable from an outage from here.
      </div>
    );
  }
  if (!data) {
    return (
      <div data-health="loading" className="text-sm text-tower-muted">
        Reading the roadmap…
      </div>
    );
  }

  const high = data.summary.gapsBySeverity.high ?? 0;
  return (
    <div
      data-health={high > 0 ? 'red' : 'clear'}
      className={`p-4 rounded-lg border ${
        high > 0 ? 'bg-error-bg border-error' : 'bg-success-bg border-success'
      }`}
    >
      <div className="text-sm font-semibold text-tower-text">
        {high > 0
          ? `${high} high-severity ${high === 1 ? 'disagreement' : 'disagreements'} between lanes`
          : 'No high-severity disagreement between lanes'}
        <span className="font-normal"> · {data.summary.gaps} gaps in total</span>
      </div>
      <div className="flex gap-x-4 gap-y-1 flex-wrap mt-2">
        {measured.map((a: AlignmentRow) => (
          <span key={a.id} className="text-[11px] font-mono text-tower-text" data-band={a.id}>
            {a.from} → {a.to}: {a.count}
          </span>
        ))}
        {unmeasured.map((a: AlignmentRow) => (
          // Rendered in the same row as the counts, with a word where a number would be. A
          // direction nobody could compute must not be invisible beside ones that were.
          <span
            key={a.id}
            className="text-[11px] font-mono text-tower-text"
            data-band={a.id}
            data-unmeasured="true"
          >
            {a.from} → {a.to}: unmeasured
          </span>
        ))}
      </div>
      <Link
        to="/roadmap"
        className="text-xs text-tower-text underline decoration-tower-accent underline-offset-2 hover:text-tower-accent transition-colors mt-2 inline-block"
      >
        Open the five-lane roadmap →
      </Link>
    </div>
  );
}

export function AdlcPage() {
  const [data, setData] = useState<Roadmap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [manifest, setManifest] = useState<ManifestState>({ state: 'loading' });

  useEffect(() => {
    let live = true;
    roadmapApi
      .get()
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));

    fetch('/viz/manifest.json')
      .then((r) => {
        if (!r.ok) throw new Error(`manifest ${r.status}`);
        return r.json();
      })
      .then((m) => {
        if (!live) return;
        const entries: ManifestEntry[] = m.entries ?? [];
        setManifest({ state: 'read', byId: new Map(entries.map((e) => [e.id, e])) });
      })
      .catch((e) => {
        if (!live) return;
        setManifest({
          state: 'unreadable',
          detail: e instanceof Error ? e.message : String(e),
        });
      });

    return () => {
      live = false;
    };
  }, []);

  const unevidenced = STAGES.filter((s) => s.evidence.length === 0);

  /** Cited gallery ids the manifest does not contain. Empty while it is still loading. */
  const unresolved = useMemo(() => {
    if (manifest.state !== 'read') return [];
    const cited = [...STAGES.flatMap((s) => s.evidence), ...CROSS_CUTTING];
    return cited.filter((e) => e.viz && !manifest.byId.has(e.viz)).map((e) => e.viz!);
  }, [manifest]);

  return (
    <div className="min-h-screen bg-tower-bg text-tower-text flex flex-col">
      <header className="h-14 bg-tower-surface border-b border-tower-border flex items-center px-6 gap-4 shrink-0">
        <Link
          to="/"
          className="text-lg font-mono font-bold text-tower-accent tracking-wider hover:text-tower-text transition-colors"
        >
          MAESTRO
        </Link>
        <div className="h-4 w-px bg-tower-border" />
        <span className="text-sm text-tower-accent font-semibold">ADLC</span>
        <div className="flex-1" />
        <HeaderNav />
      </header>

      <main className="flex-1 overflow-y-auto p-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-2xl font-bold text-tower-accent mb-1">
            How a unit of work moves through ADP
          </h1>
          <p className="text-sm text-tower-muted mb-6 max-w-3xl leading-relaxed">
            Code generation is one step; the return is in the lifecycle. These five stages are
            the spine from <span className="font-mono">ADP-PLATFORM-OVERVIEW.md §8</span>, and
            each names the artifact on this site that evidences it — or says plainly that
            nothing does.
          </p>

          <div className="mb-8">
            <Health data={data} error={error} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
            {STAGES.map((s) => (
              <StageCard key={s.id} stage={s} manifest={manifest} />
            ))}
          </div>

          {unresolved.length > 0 && (
            <div
              data-unresolved={unresolved.join(',')}
              className="p-3 mb-3 bg-error-bg border border-error rounded-lg text-xs text-tower-text leading-relaxed"
            >
              <span className="font-mono font-bold">
                {unresolved.length} cited {unresolved.length === 1 ? 'artifact' : 'artifacts'} no
                longer {unresolved.length === 1 ? 'exists' : 'exist'}
              </span>{' '}
              ({unresolved.join(', ')}). This page names its evidence by gallery id and resolves
              it against the live manifest, so a renamed or deleted visualization surfaces here
              rather than as a link that still looks right and 404s.
            </div>
          )}

          {unevidenced.length > 0 && (
            <div
              data-unevidenced={unevidenced.map((s) => s.id).join(',')}
              className="p-3 mb-8 bg-tower-surface border border-tower-border rounded-lg text-xs text-tower-muted leading-relaxed"
            >
              <span className="font-mono text-tower-text">
                {unevidenced.length} of {STAGES.length} stages are unevidenced
              </span>{' '}
              ({unevidenced.map((s) => s.name).join(', ')}). Counted here rather than left for
              the reader to notice: a five-stage story told with three stages of evidence is the
              shape of overclaim this site reports on, and this would be a poor page to make it.
            </div>
          )}

          <section>
            <h2 className="text-sm font-semibold text-tower-text mb-3 uppercase tracking-wider font-mono">
              Cross-cutting
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {CROSS_CUTTING.map((c) => (
                <div
                  key={c.viz ?? c.route}
                  className="p-4 rounded-lg border bg-tower-surface border-tower-border"
                >
                  <ul>
                    <EvidenceLink ev={c} manifest={manifest} />
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
